import { z } from "zod";
import {
  pathsMatch,
  type NavigationStage,
  type Visit,
} from "./navigation-readiness-policy";

const markerSchema = z.object({
  navigationReady: z.enum(["shell", "content", "personal"]),
  navigationStatus: z.enum(["ready", "error"]),
  navigationMode: z.enum(["html", "hydrated"]),
  navigationPath: z.string().optional(),
});

type MarkerValue = {
  readonly stage: NavigationStage;
  readonly status: "ready" | "error";
};

export function readNavigationMarker({
  current,
  marker,
  hydrated,
}: {
  readonly current: Visit;
  readonly marker: HTMLElement;
  readonly hydrated: boolean;
}): MarkerValue | null {
  const parsed = markerSchema.safeParse(marker.dataset);

  if (!parsed.success || !marker.parentElement?.getClientRects().length)
    return null;

  const {
    navigationReady: stage,
    navigationStatus: status,
    navigationMode: mode,
    navigationPath: path,
  } = parsed.data;

  if (current.settled.has(stage) || !current.stages.includes(stage))
    return null;

  if (mode === "hydrated" && !hydrated) return null;

  if (
    stage !== "shell" &&
    !markerPathMatches({ path, currentPath: current.path })
  )
    return null;

  return { stage, status };
}

function markerPathMatches({
  path,
  currentPath,
}: {
  readonly path: string | undefined;
  readonly currentPath: string;
}): boolean {
  // The old page remains visible while the next route is pending.
  return (
    path !== undefined &&
    pathsMatch({ left: path, right: location.pathname }) &&
    pathsMatch({ left: path, right: currentPath })
  );
}
